import {
  KartState,
  ItemBoxState,
  ProjectileState,
  BananaState,
  CoinState,
  PlayerInput,
  RaceResult,
  ItemType,
  MapId,
  KartId,
  TICK_MS,
  MAX_SPEED,
  KART_COLORS,
  SOLO_BOT_COUNT,
} from '@shared/types';
import { config } from '@shared/config';
import { initTrack, checkWallCollision, type InitializedTrack } from '@shared/track';
import { getMapDef, type MapDef } from '@shared/maps';
import { simulateKart, defaultNewKartFields, type PhysicsTrackContext } from '@shared/physics';
import { Simulation } from '@server/Simulation';
import { GameScene } from './rendering/Scene';
import { FollowCamera } from './rendering/Camera';
import { TrackRenderer } from './rendering/TrackRenderer';
import { KartRenderer } from './rendering/KartRenderer';
import { ItemRenderer } from './rendering/ItemRenderer';
import { GameEffects } from './rendering/GameEffects';
import { Minimap } from './rendering/Minimap';
import { SpeedEffect } from './rendering/SpeedEffect';
import { Network } from './Network';
import { InputManager } from './InputManager';
import { SoundManager } from './audio/SoundManager';
import { InkOverlay } from './rendering/InkOverlay';

// ============================================================
// Internal types
// ============================================================

interface InputRecord {
  seq: number;
  input: PlayerInput;
}

/** Interpolation buffer for remote karts */
interface RemoteKartBuffer {
  states: Array<{ time: number; state: KartState }>;
}

// ============================================================
// Game
// ============================================================

export class Game {
  private scene: GameScene;
  private camera: FollowCamera;
  private trackRenderer: TrackRenderer;
  private kartRenderer: KartRenderer;
  private itemRenderer: ItemRenderer;
  private effects: GameEffects;
  private minimap: Minimap;
  private speedEffect: SpeedEffect | null = null;
  private network: Network;
  private input: InputManager;
  private sound: SoundManager;

  private track: InitializedTrack | null = null;
  private mapDef: MapDef | null = null;

  // Our player state
  private playerId = '';
  private localKart: KartState | null = null;

  // Client prediction state
  private inputSeq = 0;
  private pendingInputs: InputRecord[] = [];
  private lastServerSeq = -1;

  // Interpolation buffer for remote karts (keyed by player id)
  private remoteBuf: Map<string, RemoteKartBuffer> = new Map();

  // Adaptive interpolation delay — measured from snapshot arrival jitter
  private interpDelay = TICK_MS * 2; // start at 2 ticks (~67ms at 30Hz)
  private snapshotArrivalTimes: number[] = [];
  private static readonly MIN_INTERP_DELAY = config.interpolation.minDelay;
  private static readonly MAX_INTERP_DELAY = config.interpolation.maxDelay;
  private static readonly JITTER_SAMPLES = config.interpolation.jitterSamples;

  // All kart states from last snapshot
  private kartStates: KartState[] = [];
  private itemStates: ItemBoxState[] = [];
  private projectileStates: ProjectileState[] = [];
  private bananaStates: BananaState[] = [];
  private coinStates: CoinState[] = [];

  // Physics track context (derived from InitializedTrack for shared physics)
  private trackCtx: PhysicsTrackContext | null = null;

  // Ink overlay for Blooper effect (created lazily, implemented by another module)
  private inkOverlay: { update(blooperTimer: number, dt: number): void; dispose(): void } | null = null;

  // Lightning flash: track previous shrinkTimer to detect rising edge
  private prevShrinkTimer = 0;
  // Item roulette: detect when roulette finishes to play reveal sound
  private prevRouletteTimer = 0;

  // Track which kart IDs are known from the server so we can manage visuals
  private knownKartIds: Set<string> = new Set();

  // Animation loop
  private animationId = 0;
  private lastFrameTime = 0;
  private running = false;
  private paused = false;

  // Local simulation (solo mode without server)
  private localSim: Simulation | null = null;
  private localAccumulator = 0; // time accumulator for fixed-step ticks
  private localAuthKart: KartState | null = null; // authoritative kart state from last tick
  private localKartId: KartId = 'speed_demon' as KartId;

  /** Called when a local (solo) race finishes */
  onLocalRaceFinish: ((rankings: RaceResult[]) => void) | null = null;

  // Snapshot handler reference so we can unbind it later
  private snapshotHandler: ((msg: any) => void) | null = null;

  // Wall-collision cooldown to avoid emitting sparks every single frame
  private wallSparkCooldown = 0;

  // Input rate-limiting for network mode (send at tick rate, not every frame)
  private inputAccumulator = 0;

  // Barrel roll state
  private barrelRollProgress = 0;
  private wasAirborne = false;
  private static readonly BARREL_ROLL_DURATION = 0.6; // seconds for a full 360°

  // Diagnostics
  private diagTimer = 0;
  private snapCount = 0;
  private frameCount = 0;

  // Adaptive quality: disable shadows if FPS stays below threshold
  private perfFrames = 0;
  private perfTimer = 0;
  private shadowsDisabled = false;

  constructor(container: HTMLElement) {
    this.scene = new GameScene(container);
    this.camera = new FollowCamera(container.clientWidth / container.clientHeight);
    this.trackRenderer = new TrackRenderer(this.scene.scene);
    this.kartRenderer = new KartRenderer(this.scene.scene);
    this.itemRenderer = new ItemRenderer(this.scene.scene);
    this.effects = new GameEffects(this.scene.scene);
    this.minimap = new Minimap();
    this.network = new Network();
    this.input = new InputManager();
    this.sound = new SoundManager();

    // Handle window resize for camera aspect ratio
    window.addEventListener('resize', this.onResize);
  }

  getNetwork(): Network {
    return this.network;
  }

  getSound(): SoundManager {
    return this.sound;
  }

  /** Start a race with the given map */
  startRace(mapId: MapId, playerId: string, kartStates: KartState[]): void {
    this.playerId = playerId;
    this.mapDef = getMapDef(mapId);
    this.track = initTrack(
      this.mapDef.controlPoints,
      this.mapDef.startPositions,
      this.mapDef.roadWidth,
      this.mapDef.rampPositions
    );

    // Build physics track context for shared simulation
    this.trackCtx = {
      path: this.track.path,
      roadWidth: this.track.roadWidth,
      ramps: this.track.ramps,
    };

    // Apply the map's visual theme (sky, fog, lights)
    this.scene.applyTheme(this.mapDef.theme);

    // Build the 3D track geometry
    this.trackRenderer.build(this.track.path, this.mapDef);

    // Create item box visuals at their track positions
    this.itemRenderer.createItemBoxes(this.track.itemPositions);

    // Set up minimap and speed effect
    this.minimap.setTrack(this.track);
    this.speedEffect = new SpeedEffect(this.camera.camera);

    // Create ink overlay for blooper effect
    if (!this.inkOverlay) {
      this.inkOverlay = new InkOverlay();
    }

    // Create kart visuals for all players present in the initial snapshot
    this.knownKartIds.clear();
    for (const k of kartStates) {
      this.kartRenderer.createKart(k.id, 'speed_demon' as KartId, k.color);
      this.knownKartIds.add(k.id);
    }

    // Set local kart from the initial snapshot
    this.localKart = kartStates.find((k) => k.id === this.playerId) || null;

    // Snap camera to starting position instantly (no lerp)
    if (this.localKart) {
      this.camera.snapTo(this.localKart.x, this.localKart.z, this.localKart.yaw);
    }

    // Reset prediction state
    this.inputSeq = 0;
    this.pendingInputs = [];
    this.lastServerSeq = -1;
    this.remoteBuf.clear();
    this.kartStates = kartStates;
    this.itemStates = [];
    this.projectileStates = [];

    // Listen for server snapshots
    this.snapshotHandler = this.onSnapshot.bind(this);
    this.network.on('snapshot', this.snapshotHandler);

    // Initialise audio and start the engine hum + map music
    this.sound.init();
    this.sound.startEngine();
    this.sound.playMapMusic(mapId);

    // Start the render/input loop
    this.running = true;
    this.lastFrameTime = performance.now();
    this.animationId = requestAnimationFrame((t) => this.gameLoop(t));
  }

  // ────────────────────────────────────────────────────────────
  // Local solo race (no server needed)
  // ────────────────────────────────────────────────────────────

  private static BOT_NAMES = [
    'Turbo Tim', 'Speedy Sarah', 'Drift King', 'Nitro Nora',
    'Flash Felix', 'Zoom Zara', 'Blaze Bob', 'Thunder Tina',
    'Rocket Rick', 'Comet Clara', 'Dash Dan',
  ];

  startLocalRace(mapId: MapId, playerName: string, kartId: KartId): void {
    const localPlayerId = 'local_player';
    this.playerId = localPlayerId;
    this.mapDef = getMapDef(mapId);
    this.track = initTrack(
      this.mapDef.controlPoints,
      this.mapDef.startPositions,
      this.mapDef.roadWidth,
      this.mapDef.rampPositions
    );

    // Build physics track context for shared simulation
    this.trackCtx = {
      path: this.track.path,
      roadWidth: this.track.roadWidth,
      ramps: this.track.ramps,
    };

    // Apply the map's visual theme
    this.scene.applyTheme(this.mapDef.theme);
    this.trackRenderer.build(this.track.path, this.mapDef);
    this.itemRenderer.createItemBoxes(this.track.itemPositions);

    // Set up minimap and speed effect
    this.minimap.setTrack(this.track);
    this.speedEffect = new SpeedEffect(this.camera.camera);

    // Create ink overlay for blooper effect
    if (!this.inkOverlay) {
      this.inkOverlay = new InkOverlay();
    }

    // Build player + bot list
    let colorIdx = 0;
    const playerData: Array<{ id: string; name: string; color: number; gridIndex: number }> = [];
    playerData.push({
      id: localPlayerId,
      name: playerName,
      color: KART_COLORS[colorIdx++],
      gridIndex: 0,
    });

    const botIds: string[] = [];
    for (let i = 0; i < SOLO_BOT_COUNT; i++) {
      const botId = `bot_${i}`;
      botIds.push(botId);
      playerData.push({
        id: botId,
        name: Game.BOT_NAMES[i % Game.BOT_NAMES.length],
        color: KART_COLORS[colorIdx++ % KART_COLORS.length],
        gridIndex: i + 1,
      });
    }

    // Initialise audio and start the engine hum + map music
    this.sound.init();
    this.sound.startEngine();
    this.sound.playMapMusic(mapId);

    // Create local simulation
    this.localSim = new Simulation(playerData, {
      onItemPickup: (playerId) => {
        if (playerId === localPlayerId) {
          this.sound.playItemPickup();
          // Emit pickup sparkle at the player kart position
          if (this.localKart) {
            this.effects.emitPickupSparkle(this.localKart.x, this.localKart.z);
          }
        }
      },
      onPlayerHit: (playerId) => {
        if (playerId === localPlayerId) this.sound.playCollision();
      },
      onLapComplete: (playerId) => {
        if (playerId === localPlayerId) this.sound.playLapComplete();
      },
      onRaceFinish: (rankings) => {
        this.onLocalRaceFinish?.(rankings);
      },
    }, mapId, botIds);

    // Run first tick to get initial kart states
    const initial = this.localSim.tick();
    const kartStates = initial.snapshot.karts;

    // Create kart visuals
    this.knownKartIds.clear();
    for (const k of kartStates) {
      this.kartRenderer.createKart(k.id, kartId, k.color);
      this.knownKartIds.add(k.id);
    }

    // Set local kart
    this.localKart = kartStates.find((k) => k.id === localPlayerId) || null;
    if (this.localKart) {
      this.camera.snapTo(this.localKart.x, this.localKart.z, this.localKart.yaw);
    }

    // Reset state
    this.inputSeq = 0;
    this.pendingInputs = [];
    this.lastServerSeq = -1;
    this.remoteBuf.clear();
    this.kartStates = kartStates;
    this.itemStates = initial.snapshot.items;
    this.projectileStates = initial.snapshot.projectiles;

    // Initialize accumulator for fixed-step simulation in game loop
    this.localAccumulator = 0;
    this.localAuthKart = this.localKart ? { ...this.localKart } : null;
    this.localKartId = kartId;

    // Start render loop
    this.running = true;
    this.lastFrameTime = performance.now();
    this.animationId = requestAnimationFrame((t) => this.gameLoop(t));
  }

  // ────────────────────────────────────────────────────────────
  // Snapshot handling (server authoritative state)
  // ────────────────────────────────────────────────────────────

  private onSnapshot(msg: any): void {
    const snap = msg as {
      type: 'snapshot';
      tick: number;
      time: number;
      karts: KartState[];
      items: ItemBoxState[];
      projectiles: ProjectileState[];
      bananas: BananaState[];
      coins: CoinState[];
      lastSeq: Record<string, number>;
    };

    this.snapCount++;

    // Log first snapshot details
    if (this.snapCount === 1) {
      console.log(`[Game] First snapshot received: tick=${snap.tick}, ${snap.karts.length} karts`);
      for (const k of snap.karts) {
        const isLocal = k.id === this.playerId;
        console.log(`  Kart ${k.id.slice(0, 8)}${isLocal ? ' (LOCAL)' : ''}: pos=(${k.x.toFixed(1)}, ${k.z.toFixed(1)}) speed=${k.speed.toFixed(1)} vx=${k.vx.toFixed(1)} vz=${k.vz.toFixed(1)}`);
      }
    }

    this.kartStates = snap.karts;
    this.itemStates = snap.items;
    this.projectileStates = snap.projectiles;
    this.bananaStates = snap.bananas ?? [];
    this.coinStates = snap.coins ?? [];

    // ---- Server reconciliation for the local player ----
    const serverLocal = snap.karts.find((k) => k.id === this.playerId);
    if (serverLocal) {
      this.lastServerSeq = snap.lastSeq[this.playerId] ?? -1;

      // Remove acknowledged inputs
      this.pendingInputs = this.pendingInputs.filter(
        (i) => i.seq > this.lastServerSeq
      );

      // Set local state to the server's authoritative state
      this.localKart = { ...serverLocal };

      // Re-apply unacknowledged inputs on top of the server state
      // (client-side prediction reconciliation)
      for (const record of this.pendingInputs) {
        this.localKart = simulateKart(
          this.localKart,
          record.input,
          TICK_MS / 1000,
          this.trackCtx,
          this.kartStates
        );
      }
    }

    // ---- Adaptive interpolation delay (jitter measurement) ----
    const now = performance.now();
    if (config.interpolation.enabled) {
      this.snapshotArrivalTimes.push(now);
      if (this.snapshotArrivalTimes.length > Game.JITTER_SAMPLES) {
        this.snapshotArrivalTimes.shift();
      }
      if (this.snapshotArrivalTimes.length >= 5) {
        // Compute jitter: variation in inter-arrival times
        const intervals: number[] = [];
        for (let i = 1; i < this.snapshotArrivalTimes.length; i++) {
          intervals.push(this.snapshotArrivalTimes[i] - this.snapshotArrivalTimes[i - 1]);
        }
        const jitters = intervals.map((iv) => Math.abs(iv - TICK_MS));
        const sorted = [...jitters].sort((a, b) => a - b);
        const p95 = sorted[Math.floor(sorted.length * 0.95)];
        // Buffer = 1 tick + P95 jitter + small margin
        const ideal = TICK_MS + p95 + 3;
        // Smooth adjustment to avoid jerking the buffer
        const sf = config.interpolation.smoothingFactor;
        this.interpDelay = this.interpDelay * sf + ideal * (1 - sf);
        this.interpDelay = Math.max(Game.MIN_INTERP_DELAY, Math.min(Game.MAX_INTERP_DELAY, this.interpDelay));
      }

      // ---- Interpolation buffer for remote karts ----
      for (const k of snap.karts) {
        if (k.id === this.playerId) continue;

        if (!this.remoteBuf.has(k.id)) {
          this.remoteBuf.set(k.id, { states: [] });
        }
        const buf = this.remoteBuf.get(k.id)!;
        buf.states.push({ time: now, state: k });

        // Keep buffer bounded
        if (buf.states.length > config.interpolation.bufferSize) {
          buf.states.shift();
        }
      }
    }

    // ---- Handle karts that joined or left mid-race ----
    const currentIds = new Set(snap.karts.map((k) => k.id));

    // Create visuals for any new karts
    for (const k of snap.karts) {
      if (!this.knownKartIds.has(k.id)) {
        this.kartRenderer.createKart(k.id, 'speed_demon' as KartId, k.color);
        this.knownKartIds.add(k.id);
      }
    }

    // Remove visuals for karts that disappeared
    for (const id of this.knownKartIds) {
      if (!currentIds.has(id)) {
        this.kartRenderer.removeKart(id);
        this.remoteBuf.delete(id);
        this.knownKartIds.delete(id);
      }
    }
  }

  // ────────────────────────────────────────────────────────────
  // Game loop (requestAnimationFrame)
  // ────────────────────────────────────────────────────────────

  private gameLoop(now: number): void {
    if (!this.running) return;
    this.animationId = requestAnimationFrame((t) => this.gameLoop(t));

    const rawDt = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    // Cap delta time to prevent spiral of death after tab switch / lag spike
    const dt = Math.min(rawDt, 0.1);

    // While paused, still render the scene (so it doesn't go blank) but don't send inputs
    if (this.paused) {
      this.updateRendering(now, 0);
      return;
    }

    // ── Local solo mode: fixed-step accumulator + client prediction ──
    if (this.localSim) {
      const playerInput = this.input.getInput();
      this.localAccumulator += dt * 1000; // accumulate in ms

      // Run fixed-step simulation ticks
      while (this.localAccumulator >= TICK_MS) {
        this.localAccumulator -= TICK_MS;
        this.inputSeq++;
        this.localSim.receiveInput(this.playerId, this.inputSeq, playerInput);

        const result = this.localSim.tick();
        const snap = result.snapshot;

        // Update authoritative states
        this.kartStates = snap.karts;
        this.itemStates = snap.items;
        this.projectileStates = snap.projectiles;
        this.bananaStates = snap.bananas ?? [];
        this.coinStates = snap.coins ?? [];
        this.localAuthKart = snap.karts.find((k) => k.id === this.playerId) || null;

        // Populate remote buffers for bot interpolation
        const tickNow = performance.now();
        for (const k of snap.karts) {
          if (k.id === this.playerId) continue;
          if (!this.remoteBuf.has(k.id)) {
            this.remoteBuf.set(k.id, { states: [] });
          }
          const buf = this.remoteBuf.get(k.id)!;
          buf.states.push({ time: tickNow, state: k });
          if (buf.states.length > 20) buf.states.shift();
        }

        // Handle new/removed karts
        const currentIds = new Set(snap.karts.map((k) => k.id));
        for (const k of snap.karts) {
          if (!this.knownKartIds.has(k.id)) {
            this.kartRenderer.createKart(k.id, this.localKartId, k.color);
            this.knownKartIds.add(k.id);
          }
        }
        for (const id of this.knownKartIds) {
          if (!currentIds.has(id)) {
            this.kartRenderer.removeKart(id);
            this.remoteBuf.delete(id);
            this.knownKartIds.delete(id);
          }
        }
      }

      // Client prediction: advance local kart by the fractional time past the last tick
      const predDt = this.localAccumulator / 1000;
      if (this.localAuthKart && !this.localAuthKart.finished) {
        this.localKart = simulateKart(this.localAuthKart, playerInput, predDt, this.trackCtx, this.kartStates);
      } else if (this.localAuthKart) {
        this.localKart = this.localAuthKart;
      }

      this.updateRendering(now, dt);
      return;
    }

    // ── Network mode: client prediction + server reconciliation ──

    // Periodic diagnostics (every 3 seconds)
    this.frameCount++;
    this.diagTimer += dt;
    if (this.diagTimer >= 3) {
      const remoteCount = this.remoteBuf.size;
      const remoteDetails: string[] = [];
      for (const [id, buf] of this.remoteBuf) {
        if (id === this.playerId) continue;
        const latestState = buf.states.length > 0 ? buf.states[buf.states.length - 1].state : null;
        remoteDetails.push(
          `${id.slice(0, 8)}: bufLen=${buf.states.length} pos=(${latestState?.x.toFixed(1) ?? '?'}, ${latestState?.z.toFixed(1) ?? '?'}) speed=${latestState?.speed.toFixed(1) ?? '?'}`
        );
      }
      console.log(`[Game] DIAG: snaps=${this.snapCount} frames=${this.frameCount} latency=${this.network.latency}ms interpDelay=${this.interpDelay.toFixed(1)}ms remoteBufs=${remoteCount}`);
      if (remoteDetails.length > 0) {
        for (const d of remoteDetails) console.log(`  Remote: ${d}`);
      }
      if (this.localKart) {
        console.log(`  Local: pos=(${this.localKart.x.toFixed(1)}, ${this.localKart.z.toFixed(1)}) speed=${this.localKart.speed.toFixed(1)} inputSeq=${this.inputSeq} pendingInputs=${this.pendingInputs.length}`);
      }
      this.diagTimer = 0;
      this.snapCount = 0;
      this.frameCount = 0;
    }

    // Capture current input
    const currentInput = this.input.getInput();

    // Rate-limit input sending to match server tick rate (30Hz)
    // instead of sending every frame (60-120Hz)
    this.inputAccumulator += dt * 1000;
    while (this.inputAccumulator >= TICK_MS) {
      this.inputAccumulator -= TICK_MS;
      this.inputSeq++;
      this.pendingInputs.push({ seq: this.inputSeq, input: currentInput });
      this.network.sendInput(this.inputSeq, currentInput);
    }

    // Client prediction: advance local kart with current input
    if (this.localKart && !this.localKart.finished) {
      this.localKart = simulateKart(this.localKart, currentInput, dt, this.trackCtx, this.kartStates);
    }

    // Render the frame
    this.updateRendering(now, dt);
  }

  // ────────────────────────────────────────────────────────────
  // Rendering updates
  // ────────────────────────────────────────────────────────────

  private updateRendering(now: number, dt: number): void {
    // ── Adaptive quality: disable shadows if FPS is consistently low ──
    if (!this.shadowsDisabled && dt > 0) {
      this.perfFrames++;
      this.perfTimer += dt;
      if (this.perfTimer >= 3) {
        const avgFps = this.perfFrames / this.perfTimer;
        if (avgFps < config.rendering.adaptiveFpsThreshold) {
          console.log(`[Game] Low FPS (${avgFps.toFixed(0)}), disabling shadows for performance`);
          this.scene.renderer.shadowMap.enabled = false;
          this.shadowsDisabled = true;
        }
        this.perfFrames = 0;
        this.perfTimer = 0;
      }
    }

    // ── Local kart ──
    if (this.localKart) {
      const k = this.localKart;

      if (!this.kartRenderer.hasKart(k.id)) {
        this.kartRenderer.createKart(k.id, 'speed_demon' as KartId, k.color);
        this.knownKartIds.add(k.id);
      }

      // Determine if the local kart is drifting: check current input drift key
      // and speed threshold (must be above 50% max speed, matching physics logic)
      const localInput = this.input.getInput();
      const localIsDrifting = localInput.drift && Math.abs(k.speed) > MAX_SPEED * 0.5;

      this.kartRenderer.updateKart(
        k.id,
        k.x,
        k.y,
        k.z,
        k.yaw,
        k.steering,
        k.speed,
        k.boostTimer > 0,
        k.stunTimer > 0,
        localIsDrifting,
        dt,
        k.shrinkTimer,
        k.starTimer,
        k.orbitItems,
        k.heldItemBehind,
        k.trickBoosting
      );

      // ── Barrel roll ──
      const isAirborne = k.y > 0.5;

      // Trigger barrel roll when launching off a ramp
      if (isAirborne && !this.wasAirborne && k.vy > 0) {
        this.barrelRollProgress = 0.001;
        this.sound.playBarrelRoll();
      }

      // Animate barrel roll
      if (this.barrelRollProgress > 0 && this.barrelRollProgress < 1) {
        this.barrelRollProgress += dt / Game.BARREL_ROLL_DURATION;
        if (this.barrelRollProgress > 1) this.barrelRollProgress = 1;
        this.kartRenderer.applyBarrelRoll(k.id, this.barrelRollProgress);
        // Emit sparkles during roll
        if (dt > 0) {
          this.effects.emitBarrelRollSparkle(k.x, k.y, k.z, k.yaw);
        }
      }

      // Reset when landed
      if (!isAirborne && this.wasAirborne) {
        this.barrelRollProgress = 0;
      }

      this.wasAirborne = isAirborne;

      // Smooth camera follow
      this.camera.update(k.x, k.z, k.yaw, dt, k.y);

      // Update engine pitch to match kart speed, or stop it when finished
      if (k.finished) {
        this.sound.stopEngine();
      } else {
        this.sound.updateEngine(k.speed, MAX_SPEED);
      }

      // Dynamic FOV based on speed
      this.camera.updateFOV(k.speed, MAX_SPEED, k.boostTimer > 0, dt);

      // Camera shake on stun
      if (k.stunTimer > 0 && dt > 0) {
        this.camera.shake(0.3, 0.4);
      }
      this.camera.updateShake(dt);

      // Ink overlay (Blooper effect)
      if (this.inkOverlay) {
        this.inkOverlay.update(k.blooperTimer, dt);
      }

      // Lightning screen flash: detect rising edge of shrinkTimer
      if (k.shrinkTimer > 0 && this.prevShrinkTimer <= 0 && dt > 0) {
        this.camera.shake(0.5, 0.6);
      }
      this.prevShrinkTimer = k.shrinkTimer;

      // Item roulette finish: play reveal sound when roulette stops
      if (this.prevRouletteTimer > 0 && k.rouletteTimer <= 0 && k.item !== null) {
        this.sound.playItemPickup();
      }
      this.prevRouletteTimer = k.rouletteTimer;

      // Speed lines overlay
      if (this.speedEffect) {
        this.speedEffect.update(k.speed, MAX_SPEED, k.boostTimer > 0, dt);
      }

      // ── Particle effects for local kart ──
      if (dt > 0) {
        // Tire dust while driving
        this.effects.emitDust(k.x, k.z, k.yaw, k.speed, dt);

        // Boost flames while boosting
        if (k.boostTimer > 0) {
          this.effects.emitBoostParticles(k.x, k.z, k.yaw, dt);
        }

        // Wall collision sparks (check collision in rendering step with cooldown)
        this.wallSparkCooldown -= dt;
        if (this.track && Math.abs(k.speed) > 2) {
          const wall = checkWallCollision(
            this.track.path,
            k.x,
            k.z,
            this.track.roadWidth / 2
          );
          if (wall.hit && this.wallSparkCooldown <= 0) {
            this.effects.emitWallSparks(wall.pushX, wall.pushZ, wall.normalX, wall.normalZ);
            this.wallSparkCooldown = 0.15; // min 150ms between spark bursts
          }
        }
      }
    }

    // ── Remote karts: render directly from latest snapshot data ──
    for (const k of this.kartStates) {
      if (k.id === this.playerId) continue;

      if (!this.kartRenderer.hasKart(k.id)) {
        this.kartRenderer.createKart(k.id, 'speed_demon' as KartId, k.color);
        this.knownKartIds.add(k.id);
      }

      const remoteIsDrifting = Math.abs(k.steering) > 0.3 && Math.abs(k.speed) > MAX_SPEED * 0.5;
      this.kartRenderer.updateKart(
        k.id,
        k.x,
        k.y,
        k.z,
        k.yaw,
        k.steering,
        k.speed,
        k.boostTimer > 0,
        k.stunTimer > 0,
        remoteIsDrifting,
        dt,
        k.shrinkTimer,
        k.starTimer,
        k.orbitItems,
        k.heldItemBehind,
        k.trickBoosting
      );
    }

    // ── Item boxes ──
    if (this.itemStates.length > 0) {
      this.itemRenderer.updateItemBoxes(this.itemStates);
    }

    // ── Projectiles ──
    this.itemRenderer.updateProjectiles(this.projectileStates);

    // ── Bananas ──
    this.itemRenderer.updateBananas(this.bananaStates);

    // ── Coins ──
    this.itemRenderer.updateCoins(this.coinStates);

    // ── Animate items (bobbing, rotation) ──
    this.itemRenderer.animate(now / 1000);

    // ── Animate track shader uniforms (road patterns, neon walls, ground) ──
    this.trackRenderer.animate(now / 1000);

    // ── Update particle effects ──
    this.effects.update(dt);

    // ── Update minimap ──
    this.minimap.update(this.localKart, this.kartStates, this.itemStates);

    // ── Render the scene (via composer when post-processing is active) ──
    this.scene.render(this.camera.camera);
  }

  // ────────────────────────────────────────────────────────────
  // Public accessors for HUD
  // ────────────────────────────────────────────────────────────

  /** Get current predicted local kart state */
  getLocalKart(): KartState | null {
    return this.localKart;
  }

  /** Get all kart states (for leaderboard) */
  getAllKarts(): KartState[] {
    return this.kartStates;
  }

  /** Get the latest measured round-trip latency */
  getLatency(): number {
    return this.network.latency;
  }

  /** Pause the game (stop sending inputs, keep rendering) */
  pause(): void {
    this.paused = true;
    this.sound.setMuted(true);
  }

  /** Resume the game */
  resume(): void {
    this.paused = false;
    this.sound.setMuted(false);
    this.lastFrameTime = performance.now(); // avoid a huge dt spike
  }

  // ────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────

  private onResize = (): void => {
    this.scene.resize();
    this.camera.resize(this.scene.getAspect());
  };

  /** Stop the race and clean up scene objects, but keep renderer and network alive */
  stop(): void {
    this.running = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = 0;
    }

    // Stop engine sound and music
    this.sound.stopEngine();
    this.sound.stopMusic();

    // Stop local simulation
    this.localSim = null;
    this.localAccumulator = 0;
    this.localAuthKart = null;

    // Unbind snapshot handler
    if (this.snapshotHandler) {
      this.network.off('snapshot', this.snapshotHandler);
      this.snapshotHandler = null;
    }

    // Clean up particle effects and overlays
    this.effects.dispose();
    if (this.speedEffect) {
      this.speedEffect.dispose();
      this.speedEffect = null;
    }
    this.minimap.clear();

    // Clear all 3D objects
    this.kartRenderer.clearAll();
    this.itemRenderer.clear();
    this.trackRenderer.clear();
    this.scene.clearScene();

    // Re-create effects for potential next race (since scene was cleared)
    this.effects = new GameEffects(this.scene.scene);

    // Reset game state
    this.paused = false;
    this.pendingInputs = [];
    this.remoteBuf.clear();
    this.snapshotArrivalTimes = [];
    this.interpDelay = TICK_MS * 2;
    this.knownKartIds.clear();
    this.localKart = null;
    this.kartStates = [];
    this.itemStates = [];
    this.projectileStates = [];
    this.bananaStates = [];
    this.coinStates = [];
    this.trackCtx = null;
    this.prevShrinkTimer = 0;
    this.prevRouletteTimer = 0;
    if (this.inkOverlay) {
      this.inkOverlay.dispose();
      this.inkOverlay = null;
    }
    this.track = null;
    this.mapDef = null;
    this.inputSeq = 0;
    this.lastServerSeq = -1;
    this.wallSparkCooldown = 0;
    this.barrelRollProgress = 0;
    this.wasAirborne = false;
    this.inputAccumulator = 0;
  }

  /** Full disposal -- call when the game object is being destroyed */
  dispose(): void {
    this.stop();
    this.effects.dispose();
    this.sound.dispose();
    this.network.close();
    this.input.dispose();
    this.scene.dispose();
    window.removeEventListener('resize', this.onResize);
  }
}
